import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Box, Stack, Switch, TextField, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

import BaseCalendar from "../../../../component/calendar/BaseCalendar";
import ActionButton from "../../../../component/common/ActionButton";
import SearchField from "../../../../component/common/SearchField";
import FormModal from "../../../../component/common/FormModal";
import FormField from "../../../../component/common/FormField";
import ComboBox from "../../../../component/common/ComboBox";
// fmtKRWFull(전체 자릿수) 을 쓴다 — fmtKRW 는 만/억으로 줄이는 축약 포맷이라 "10만" 처럼 보인다.
// 고정비는 실제 지급액을 확인하는 화면이므로 100,000원 처럼 원 단위까지 그대로 보여야 한다.
import { financeApi, fmtKRWFull } from "../../api/financeApi";
import { runApiAction } from "../../../../util/apiFeedback";
import { confirm as confirmDialog } from "../../../../util/confirmDialog";

/** 입력값에서 숫자만 남긴다 — 콤마·공백·문자를 걷어낸 '저장용' 값. */
const digitsOnly = (v) => String(v ?? "").replace(/[^\d]/g, "");

/** 숫자를 3자리 콤마로 — 화면 표시용. 빈 값이면 빈 문자열(placeholder 가 보이게). */
const withCommas = (v) => {
    const d = digitsOnly(v);
    return d ? Number(d).toLocaleString("ko-KR") : "";
};

/** 날짜 칸 안 섹션 — 출금이 먼저. 실제 데이터가 지출 위주라 자주 보는 쪽을 위에 둔다. */
const SECTIONS = [
    { key: "EXPENSE", label: "출금", income: false },
    { key: "INCOME",  label: "입금", income: true  },
];

/** 템플릿 묶음의 순액 합계 — 수입(INCOME)은 +, 지출(EXPENSE)은 −로 계산한다. */
const sumOf = (list) => (list || []).reduce(
    (acc, t) => acc + (t.flowType === "INCOME" ? Number(t.amount || 0) : -Number(t.amount || 0)), 0);

/** 그 달의 실제 지급일(payDay, 말일 초과 시 말일로 보정) */
const payDayOf = (t, monthDay) => Math.min(t.payDay || 1, monthDay.daysInMonth());

/** day(dayjs)가 템플릿 유효 기간[startDate,endDate] 안인지 — null 이면 무기한 */
const inPeriod = (t, day) =>
    (!t.startDate || !day.isBefore(dayjs(t.startDate), "day")) &&
    (!t.endDate   || !day.isAfter(dayjs(t.endDate),   "day"));

// vendorId 를 반드시 함께 들고 다녀야 한다.
//   [기존 버그] 이 폼이 vendorName(자유 텍스트)만 다루고 vendorId 를 안 보내서, 달력에서 고정비를
//   수정하면 서버 applyRecurring 이 vendor 를 null 로 덮어써 거래처 연결이 끊겼다
//   ('현금 흐름표 > 고정비 관리'에서 연결해 둔 거래처가 사라짐).
const EMPTY_FORM = {
    id: null, flowType: "EXPENSE", title: "", amount: "", payDay: 1,
    startDate: "", endDate: "", vendorId: null, vendorName: "", category: "고정비/관리비",
    note: "", active: true,
};

/**
 * 고정비 달력 — 고정비(반복지출) 템플릿을 달력에서 보고 수기로 추가·편집한다.
 *
 * <p>각 고정비는 유효 기간(시작~종료일)과 매월 지급일(payDay)을 가진다. 달력의 각 날짜 셀에는
 * 그 달이 기간 안이고 그 날이 지급일인 고정비를 금액 배지로 표시한다(사용자 확정: 지급일 배지만,
 * 기간 가로 막대 없음). 배지/버튼으로 추가·수정·삭제하며 기존 recurring 템플릿 CRUD API를 재사용한다.
 */
export default function FixedCostCalendarTab() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [anchor, setAnchor]       = useState(() => dayjs());
    const [form, setForm]           = useState(null);   // null = 닫힘, 객체 = 폼 열림
    const [saving, setSaving]       = useState(false);
    const [search, setSearch]       = useState("");     // 달력 검색 — 매칭 항목만 활성화(강조), 나머지는 흐리게

    // 표가 아니라 달력이라 '필터'가 아닌 '강조'다 — 어차피 이번 달 데이터를 다 들고 있으므로,
    // 검색어가 있으면 매칭 배지만 도드라지게(활성화) 하고 나머지는 흐린다. 합계는 실제 값 그대로 둔다.
    const searchQ = search.trim().toLowerCase();
    const matchesSearch = (t) => !searchQ ||
        [t.title, t.vendorName, t.category, t.note, String(t.amount)]
            .some(v => String(v ?? "").toLowerCase().includes(searchQ));

    const load = useCallback(() => {
        setLoading(true);
        financeApi.getRecurringTemplates()
            .then(data => setTemplates(Array.isArray(data) ? data : []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const activeTemplates = useMemo(() => templates.filter(t => t.active), [templates]);

    /**
     * 이번 달 합계 — 달력에 그려지는 항목만 더한다.
     *
     * <p>템플릿 전체를 더하면 안 된다. 유효 기간이 끝났거나 아직 시작 안 한 템플릿, 말일 보정으로
     * 이 달에 걸리지 않는 건이 섞여 <b>화면의 주간 합계를 모두 더한 값과 어긋나기</b> 때문이다.
     * 날짜 칸을 채울 때와 똑같은 조건(payDayOf + inPeriod)으로 다시 훑는다.
     */
    const monthHits = useMemo(() => {
        const start = anchor.startOf("month");
        const out = [];
        for (let i = 0; i < anchor.daysInMonth(); i++) {
            const day = start.add(i, "day");
            out.push(...activeTemplates.filter(t => day.date() === payDayOf(t, day) && inPeriod(t, day)));
        }
        return out;
    }, [activeTemplates, anchor]);

    // 달력 셀 배지 — 그 달·그 날 지급일인 고정비들
    const renderDayBadges = (day, { isCurrentMonth }) => {
        if (!isCurrentMonth) return null;   // 인접 달 셀엔 표시 안 함(중복 방지)
        const hits = activeTemplates.filter(t => day.date() === payDayOf(t, day) && inPeriod(t, day));
        if (hits.length === 0) return null;
        // Stack 이 아니라 Box + gap 을 쓴다.
        //   MUI Stack 의 spacing 은 자식에게 margin-top 을 직접 걸어서, 합계 줄의 'mt:auto'(바닥에 붙이기)를
        //   덮어쓴다 — 그래서 합계가 항목 바로 뒤에 남아 칸마다 높이가 제각각이었다.
        //   gap 은 margin 을 건드리지 않으므로 mt:auto 와 충돌하지 않는다.
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, mt: 0.3, flex: 1, minHeight: 0 }}>
                {/*
                  * 출금/입금 섹션으로 나눠 보여준다.
                  *
                  * 항목마다 부호를 붙이면 구분자 '-' 와 마이너스 '-' 가 겹쳐 "항목 - -390,000원" 처럼
                  * 대시가 두 번 나오는데, 데이터가 대부분 지출이라 거의 모든 줄이 그렇게 보인다.
                  * 방향은 섹션 머리말이 한 번만 말해주고, 각 줄은 '항목명 - 금액' 으로 깔끔하게 둔다.
                  *
                  * 색 배지를 쓰지 않는 이유도 같다 — 한 날짜에 여러 건이 겹치면 달력이 색 덩어리가 되어
                  * 정작 '무엇이 언제 나가는지' 가 안 읽힌다. 강조는 '지금 가리키는 줄' 하나에만 준다.
                  */}
                {SECTIONS.map(sec => {
                    const items = hits.filter(t => (t.flowType === "INCOME") === sec.income);
                    if (items.length === 0) return null;   // 없는 방향의 머리말로 칸을 낭비하지 않는다
                    return (
                        <Box key={sec.key}>
                            <Typography sx={{
                                fontSize: 9.5, fontWeight: 800, color: "#94a3b8",
                                letterSpacing: 0.2, px: 0.6, mb: 0.1,
                            }}>
                                {sec.label}
                            </Typography>
                            {items.map(t => {
                                const matched = matchesSearch(t);
                                return (
                                <Box key={t.id}
                                    onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                                    title={`${sec.label} · ${t.title} - ${fmtKRWFull(t.amount)}`}
                                    sx={{
                                        px: 0.6, py: 0.2, borderRadius: 0.8, cursor: "pointer",
                                        fontSize: 10.5, lineHeight: 1.25,
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                        // 검색 중이면 매칭 항목을 '활성화'(호박색·굵게)하고 비매칭은 흐린다.
                                        // 검색이 없으면 기존 그대로(회색). 호버한 '한 줄'만 파랗게 강조되는 동작도 유지.
                                        fontWeight: searchQ && matched ? 800 : 600,
                                        color:   searchQ && matched ? "#854d0e" : "#475569",
                                        bgcolor: searchQ && matched ? "#fef9c3" : "transparent",
                                        opacity: searchQ && !matched ? 0.3 : 1,
                                        transition: "background-color .12s, color .12s, opacity .12s",
                                        "&:hover": { bgcolor: "#eff6ff", color: "#1d4ed8", opacity: 1 },
                                    }}>
                                    {t.title} - {fmtKRWFull(t.amount)}
                                </Box>
                                );
                            })}
                        </Box>
                    );
                })}

                {/* 일간 합계 — 주간 합계와 <b>같은 회색 띠</b>로 맞춘다.
                    항목 줄과 같은 흰 바탕에 두면 그게 합계인지 마지막 항목인지 구분이 안 된다.
                    주간 합계처럼 배경을 깔아 '요약 줄' 임을 한눈에 알게 하고, 두 합계가 같은 언어로 읽히게 한다.

                    mx/px 의 음수 여백은 셀 패딩(BaseCalendar 의 p:0.6)을 상쇄해 띠가 칸 끝까지 닿게 하려는 것 —
                    안쪽에만 깔면 좌우가 뜬 채로 보여 주간 합계 띠와 결이 달라진다. */}
                <Box sx={{
                    // mt:auto — 항목이 1건이든 7건이든 합계 줄은 칸 바닥에 붙는다.
                    // 그래야 한 주 안에서 합계 줄들이 같은 높이로 정렬돼, 주간 합계 띠 바로 위에 나란히 놓인다.
                    mt: "auto", mx: -0.6, mb: -0.6, px: 0.8, py: 0.35,
                    borderTop: "0.5px solid #e2e8f0",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5,
                }}>
                    <Typography sx={{
                        fontSize: 10, fontWeight: 700, color: "#94a3b8",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                        합계 ({hits.length}건)
                    </Typography>
                    <Typography sx={{
                        fontSize: 10.5, fontWeight: 800, color: "#0f172a",
                        whiteSpace: "nowrap", flexShrink: 0,
                    }}>
                        {fmtKRWFull(sumOf(hits))}
                    </Typography>
                </Box>
            </Box>
        );
    };

    /**
     * 주 단위 합계 줄 — 그 주(월~일)에 지급일이 걸린 고정비 총액.
     *
     * <p>일별 합계만으로는 "이번 주에 얼마 나가는지" 를 알려면 7칸을 눈으로 더해야 한다.
     * 자금 계획은 보통 주 단위로 세우므로 행마다 한 줄을 둔다. 그 주에 아무것도 없으면
     * 빈 줄로 달력을 늘리지 않고 아예 그리지 않는다.
     */
    const renderWeekFooter = (weekDays) => {
        // 인접 달 셀은 날짜 칸에서도 표시하지 않으므로(중복 방지) 합계에서도 뺀다 —
        // 그래야 화면에 보이는 항목의 합과 이 숫자가 일치한다.
        const inMonth = weekDays.filter(d => d.month() === anchor.month());
        const hits = inMonth.flatMap(day =>
            activeTemplates.filter(t => day.date() === payDayOf(t, day) && inPeriod(t, day)));
        if (hits.length === 0) return null;
        return (
            <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1,
                px: 1.2, py: 0.5,
                bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0",
            }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "red" }}>
                    주간 합계 ({hits.length}건) {fmtKRWFull(sumOf(hits))}
                </Typography>
            </Box>
        );
    };

    // ── 폼 열기/저장/삭제 ──
    const openCreate = () => setForm({ ...EMPTY_FORM, startDate: anchor.startOf("month").format("YYYY-MM-DD") });

    /**
     * 날짜 칸 클릭 → 그 날짜를 '매월 지급일' 로 채운 추가 폼.
     *
     * <p>항목 줄을 누르면 그 항목 편집(openEdit)이 열리고 이벤트가 거기서 멈추므로,
     * 여기로 오는 건 '빈 곳을 눌렀을 때' 뿐이다. 25일 칸을 눌렀다면 매월 25일 고정비를 만들려는
     * 의도가 대부분이라, 지급일을 미리 채워 입력 한 번을 줄인다.
     */
    const openCreateOnDay = (day) => setForm({
        ...EMPTY_FORM,
        payDay: day.date(),
        startDate: day.startOf("month").format("YYYY-MM-DD"),
    });
    const openEdit = (t) => setForm({
        id: t.id, flowType: t.flowType || "EXPENSE", title: t.title || "",
        amount: t.amount ?? "", payDay: t.payDay ?? 1,
        startDate: t.startDate || "", endDate: t.endDate || "",
        vendorId: t.vendorId ?? null,                 // 거래처 연결 보존(누락 시 서버가 끊음)
        vendorName: t.vendorName || "", category: t.category || "고정비/관리비",
        note: t.note || "", active: t.active !== false,
    });
    const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const canSave = form && form.title.trim() && Number(digitsOnly(form.amount)) > 0
        && Number(form.payDay) >= 1 && Number(form.payDay) <= 31;

    const handleSave = () => {
        const payload = {
            flowType: form.flowType,
            title: form.title.trim(),
            amount: Number(digitsOnly(form.amount)),
            payDay: Number(form.payDay),
            startDate: form.startDate || null,
            endDate: form.endDate || null,
            vendorId: form.vendorId ?? null,          // 기존 거래처 연결 유지
            vendorName: form.vendorName.trim() || null,
            category: form.category.trim() || "고정비/관리비",
            note: form.note.trim() || null,
            active: form.active,
        };
        runApiAction({
            request: () => form.id
                ? financeApi.updateRecurringTemplate(form.id, payload)
                : financeApi.createRecurringTemplate(payload),
            successMsg: form.id ? "고정비를 수정했습니다." : "고정비를 등록했습니다.",
            errorMsg: (m) => "저장 실패: " + (m || "알 수 없는 오류"),
            setLoading: setSaving,
            onSuccess: () => { setForm(null); load(); },
        });
    };

    const handleDelete = async () => {
        if (!form?.id) return;
        if (!(await confirmDialog({ message: `고정비 "${form.title}"을(를) 삭제하시겠습니까?` }))) return;
        runApiAction({
            request: () => financeApi.deleteRecurringTemplate(form.id),
            successMsg: "고정비를 삭제했습니다.",
            errorMsg: (m) => "삭제 실패: " + (m || "알 수 없는 오류"),
            setLoading: setSaving,
            onSuccess: () => { setForm(null); load(); },
        });
    };

    return (
        <Box sx={{paddingBottom: 10}}>
            <BaseCalendar
                anchor={anchor}
                onAnchorChange={setAnchor}
                view="month"
                supportedViews={["month"]}
                loading={loading}
                onRefresh={load}
                cellHeight={132}
                onDayClick={openCreateOnDay}
                renderDayBadges={renderDayBadges}
                renderWeekFooter={renderWeekFooter}
                toolbarLeft={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, ml: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#94a3b8" }}>
                            이번 달 합계 ({monthHits.length}건)
                        </Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>
                            {fmtKRWFull(sumOf(monthHits))}
                        </Typography>
                    </Box>
                }
                toolbarRight={
                    <Stack direction="row" spacing={1} alignItems="center">
                        <SearchField value={search} onChange={setSearch}
                            placeholder="항목·거래처·분류 검색" width={200} />
                        <ActionButton
                            variant="primary"
                            startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={openCreate}
                            label="고정비 추가"
                            sx={{ fontSize: 12 }}
                        />
                    </Stack>
                }
            />

            {/* 추가/편집 폼 */}
            <FormModal
                open={Boolean(form)}
                onClose={() => setForm(null)}
                title={form?.id ? "고정비 수정" : "고정비 추가"}
                subtitle="유효 기간과 매월 지급일을 설정하면 달력에 표시됩니다."
                maxWidth="sm"
                onSave={handleSave}
                saving={saving}
                saveDisabled={!canSave}
                footerExtras={form?.id && (
                    <ActionButton variant="delete" onClick={handleDelete} disabled={saving} label="삭제" sx={{ fontSize: 12 }} />
                )}
            >
                {form && (
                    <Stack spacing={2}>
                        {/* 항목명이 먼저, 구분이 뒤 — 사람이 채우는 순서(무엇을 → 어떤 성격으로)와 같게.
                            폭은 아래 '시작일/종료일' 줄과 눈금이 맞도록 flex 로 잡고, 구분만 좁게 고정한다. */}
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <FormField label="항목명" sx={{ flex: 1, minWidth: 0 }}>
                                <TextField size="small" fullWidth placeholder="예: 사무실 임대료"
                                    value={form.title} onChange={(e) => setF("title", e.target.value)} />
                            </FormField>
                            <FormField label="구분" sx={{ width: 130, flexShrink: 0 }}>
                                <ComboBox
                                    options={[{ value: "EXPENSE", label: "지출" }, { value: "INCOME", label: "수입" }]}
                                    value={form.flowType} onChange={(v) => setF("flowType", v)} clearable={false} />
                            </FormField>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            {/* 금액은 3자리 콤마로 보여준다. type="number" 로는 콤마를 넣을 수 없어 text 로 두고,
                                입력 중엔 숫자만 남겨 서식을 다시 입히며 저장 시엔 콤마를 떼고 숫자로 보낸다. */}
                            <FormField label="금액 (원)" sx={{ flex: 1, minWidth: 0 }}>
                                <TextField size="small" fullWidth placeholder="1,500,000"
                                    value={withCommas(form.amount)}
                                    onChange={(e) => setF("amount", digitsOnly(e.target.value))}
                                    inputProps={{ inputMode: "numeric" }} />
                            </FormField>
                            <FormField label="매월 지급일 (1~31)" sx={{ width: 130, flexShrink: 0 }}>
                                <TextField size="small" fullWidth type="number" inputProps={{ min: 1, max: 31 }}
                                    value={form.payDay} onChange={(e) => setF("payDay", e.target.value)} />
                            </FormField>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <FormField label="시작일 (비우면 무기한)" sx={{ flex: 1, minWidth: 0 }}>
                                <TextField size="small" fullWidth type="date"
                                    value={form.startDate} onChange={(e) => setF("startDate", e.target.value)}
                                    InputLabelProps={{ shrink: true }} />
                            </FormField>
                            <FormField label="종료일 (비우면 무기한)" sx={{ flex: 1, minWidth: 0 }}>
                                <TextField size="small" fullWidth type="date"
                                    value={form.endDate} onChange={(e) => setF("endDate", e.target.value)}
                                    InputLabelProps={{ shrink: true }} />
                            </FormField>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <FormField label="거래처 (선택)" sx={{ flex: 1, minWidth: 0 }}>
                                {/* 이름을 직접 고치면 기존 거래처 연결(vendorId)은 해제한다 — 자유입력으로 전환하는 의미.
                                    그대로 두고 저장하면 연결이 유지된다(위 openEdit 이 vendorId 를 보존). */}
                                <TextField size="small" fullWidth placeholder="거래처명"
                                    value={form.vendorName}
                                    onChange={(e) => setForm(prev => ({ ...prev, vendorName: e.target.value, vendorId: null }))} />
                            </FormField>
                            <FormField label="분류" sx={{ flex: 1, minWidth: 0 }}>
                                <TextField size="small" fullWidth
                                    value={form.category} onChange={(e) => setF("category", e.target.value)} />
                            </FormField>
                        </Box>

                        <FormField label="비고">
                            <TextField size="small" fullWidth multiline minRows={2}
                                value={form.note} onChange={(e) => setF("note", e.target.value)} />
                        </FormField>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Switch checked={form.active} onChange={(e) => setF("active", e.target.checked)} size="small" />
                            <Typography sx={{ fontSize: 13, color: "#475569" }}>
                                활성 (끄면 달력·월별 생성에서 제외)
                            </Typography>
                        </Box>
                    </Stack>
                )}
            </FormModal>
        </Box>
    );
}
